import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { TEAM_FEATURED, TEAM_MEMBERS } from "@/data/team";
import useSEO, { BASE_URL, DEFAULT_OG_IMAGE } from "@/hooks/useSEO";

export default function QuienesSomos() {
  useSEO({
    title: "Quiénes somos · C.D. La Bombonera de Barcelona",
    description:
      "Conocé a las personas detrás del C.D. La Bombonera de Barcelona: directiva, cuerpo técnico, staff y el fundador Gonzalo Castro.",
    canonical: `${BASE_URL}/quienes-somos`,
    image: DEFAULT_OG_IMAGE,
  });
  // Group members for sectioning
  const groups = TEAM_MEMBERS.reduce((acc, m) => {
    (acc[m.group] = acc[m.group] || []).push(m);
    return acc;
  }, {});
  const groupOrder = ["Directiva", "Cuerpo técnico", "Staff"];

  return (
    <main data-testid="quienes-somos-page" className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-6 flex items-center gap-3">
          <span className="h-px w-10 bg-[#FFB800]" />
          Quiénes somos
        </p>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-extrabold uppercase tracking-tight leading-[0.9]">
          Los socios<br />
          <span className="italic font-light text-white/70">de La Bombonera.</span>
        </h1>
        <p className="text-white/60 max-w-2xl mt-8 text-lg leading-relaxed">
          Detrás de cada partido hay un grupo humano. Conocé a las personas
          que sostienen el proyecto día a día.
        </p>

        {/* Team grid by group */}
        <div className="mt-20 space-y-20">
          {groupOrder.map((groupName, gIdx) => {
            const list = groups[groupName] || [];
            if (list.length === 0) return null;
            return (
              <section
                key={groupName}
                data-testid={`team-group-${groupName.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="flex items-end justify-between mb-10 gap-6">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-3 flex items-center gap-3">
                      <span className="h-px w-10 bg-[#FFB800]" />
                      0{gIdx + 1} — {groupName}
                    </p>
                    <h3 className="font-display text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
                      {groupName === "Directiva" && "Quienes lideran."}
                      {groupName === "Cuerpo técnico" && "Quienes dirigen el juego."}
                      {groupName === "Staff" && "Quienes lo hacen posible."}
                    </h3>
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40">
                    {String(list.length).padStart(2, "0")} miembros
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {list.map((m) => (
                    <article
                      key={m.slug}
                      data-testid={`team-card-${m.slug}`}
                      className="group relative rounded-[2rem] overflow-hidden bg-[#0A1121] border border-white/8 hover:border-[#FFB800]/40 transition-all duration-500"
                    >
                      <div className="aspect-[3/4] overflow-hidden relative bg-[#0B1D3A]">
                        {m.noPhoto ? (
                          <div
                            data-testid={`team-card-${m.slug}-noimg`}
                            className="absolute inset-0 bg-gradient-to-br from-[#0B1D3A] via-[#0F2347] to-[#081630]"
                          >
                            {/* Subtle radial highlight */}
                            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.06),transparent_60%)]" />
                            {/* Big initial as watermark */}
                            <div className="absolute inset-0 grid place-items-center">
                              <span className="font-display font-extrabold text-white/8 text-[10rem] leading-none select-none">
                                {m.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            {/* Status ribbon */}
                            <div className="absolute top-5 left-5 rounded-full bg-[#FFB800]/15 border border-[#FFB800]/30 text-[#FFB800] px-3 py-1 font-mono text-[9px] uppercase tracking-[0.25em]">
                              Próximamente
                            </div>
                          </div>
                        ) : (
                          <img
                            src={m.photo}
                            alt={m.name}
                            loading="lazy"
                            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
                          />
                        )}
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-[#030712] via-[#030712]/30 to-transparent pointer-events-none" />
                      <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between gap-3">
                        <div className="flex flex-col">
                          <span className="text-[10px] uppercase tracking-[0.25em] text-[#FFB800] mb-1">
                            {m.role}
                          </span>
                          <h4 className="font-display text-xl md:text-2xl font-extrabold uppercase tracking-tight text-white group-hover:text-[#FFB800] transition-colors">
                            {m.name}
                          </h4>
                        </div>
                        <span
                          className="h-9 w-9 rounded-full grid place-items-center border border-white/15 group-hover:border-[#FFB800] flex-shrink-0"
                          aria-hidden
                        >
                          <span className="block w-1.5 h-1.5 rounded-full bg-[#FFB800] group-hover:scale-125 transition-transform" />
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* Featured founder — at the end */}
        <section
          data-testid="team-featured"
          className="mt-28"
        >
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-[#FFB800]" />
            04 — Por esta razón empezó todo
          </p>

          <div className="relative rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-[#0A1121] to-[#030712] p-6 md:p-10 lg:p-14 overflow-hidden">
            {/* Ambient portrait — Gonzalo's photo blurred as background */}
            <img
              src={TEAM_FEATURED.photo}
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-35 pointer-events-none"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#030712]/85 via-[#030712]/75 to-[#030712]/90 pointer-events-none" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,184,0,0.08),transparent_60%)] pointer-events-none" />

            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-5 relative order-2 lg:order-1">
                <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden bg-[#0A1121]">
                  <img
                    src={TEAM_FEATURED.photo}
                    alt={TEAM_FEATURED.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030712]/70 via-transparent to-transparent" />
                  <div className="absolute inset-0 ring-1 ring-[#FFB800]/20 rounded-[2rem] pointer-events-none" />
                </div>

                {/* Floating badge */}
                <div className="absolute -top-4 -left-4 md:-top-6 md:-left-6 w-24 h-24 md:w-28 md:h-28 rounded-full bg-[#FFB800] grid place-items-center text-[#030712] shadow-xl rotate-[-8deg]">
                  <div className="text-center leading-tight">
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em]">Desde</p>
                    <p className="font-display text-2xl font-extrabold">2015</p>
                    <p className="font-mono text-[9px] uppercase tracking-[0.25em]">BCN</p>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7 order-1 lg:order-2">
                <span className="rounded-full border border-[#FFB800]/40 bg-[#FFB800]/10 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB800] inline-block">
                  Fundador
                </span>
                <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] mt-5">
                  {TEAM_FEATURED.name}
                </h2>
                <p className="mt-2 text-[12px] uppercase tracking-[0.25em] text-white/50">
                  {TEAM_FEATURED.role}
                </p>
                <p className="mt-8 text-white/75 text-[17px] leading-[1.85]">
                  {TEAM_FEATURED.bio}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer CTA */}
        <div
          data-testid="team-cta"
          className="mt-28 rounded-[2.5rem] border border-white/10 bg-gradient-to-br from-[#0A1121] to-[#030712] p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-8"
        >
          <div className="max-w-2xl">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-4">
              ¿Querés sumarte?
            </p>
            <h3 className="font-display text-3xl md:text-5xl font-extrabold uppercase tracking-tight leading-[0.95]">
              Hacé crecer<br />
              <span className="italic font-light text-white/70">
                el club con nosotros.
              </span>
            </h3>
          </div>
          <Link
            to="/contacto"
            data-testid="team-contacto-cta"
            className="btn-primary self-start md:self-auto flex-shrink-0"
          >
            Escribinos
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </main>
  );
}
